import { useEffect, useRef, useState } from 'react'
import { API_BASE, SearchResult, colorForType } from './types'

interface SearchBarProps {
  onResults: (ids: Set<string> | null) => void
  onSelect: (id: string) => void
}

export default function SearchBar({ onResults, onSelect }: SearchBarProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current)

    const q = query.trim()
    if (q.length === 0) {
      setResults([])
      setOpen(false)
      onResults(null)
      return
    }

    // Debounce 300ms
    timer.current = window.setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetch(
          `${API_BASE}/api/notes/search?q=${encodeURIComponent(q)}`,
        )
        const data = await res.json()
        const list: SearchResult[] = data.results || []
        setResults(list)
        setOpen(true)
        onResults(new Set(list.map((r) => r.id)))
      } catch (err) {
        console.error('Búsqueda fallida:', err)
        setResults([])
        onResults(new Set())
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => {
      if (timer.current) window.clearTimeout(timer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const clear = () => {
    setQuery('')
    setResults([])
    setOpen(false)
    onResults(null)
  }

  return (
    <div className="search-bar">
      <div className="search-input-wrap">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          value={query}
          placeholder="Buscar notas…"
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
        />
        {query && (
          <button className="search-clear" onClick={clear} aria-label="Limpiar">
            ×
          </button>
        )}
      </div>

      {open && (
        <div className="search-results">
          {loading && <div className="search-status">Buscando…</div>}
          {!loading && results.length === 0 && (
            <div className="search-status">Sin resultados</div>
          )}
          {results.map((r) => (
            <button
              key={r.id}
              className="search-result"
              onClick={() => {
                onSelect(r.id)
                setOpen(false)
              }}
            >
              <span
                className="type-dot small"
                style={{ background: colorForType(r.note_type) }}
              />
              <span className="search-result-title">{r.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
